import React, { useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import TeleconsultIcon from "@mui/icons-material/MedicalServices";
import SettingsIcon from "@mui/icons-material/Settings";
import InfoIcon from "@mui/icons-material/Info";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import LogoutIcon from "@mui/icons-material/Logout";
import logo from "../../assets/logo.png";
import "./Navbar.css";

const Navbar: React.FC = () => {
  const [state, setState] = React.useState({ right: false });
  const [selectedMenu, setSelectedMenu] = React.useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();
  const { refNumber } = useParams();

  // Menu items for navigation
  const menuItems = [
    { text: "Teleconsult", icon: <TeleconsultIcon />, route: "/home/teleconsult" },
    { text: "Accounts", icon: <InfoIcon />, route: "/home/accounts" },
    { text: "Masters", icon: <DashboardIcon />, route: "/home/masters" },
    { text: "Settings", icon: <SettingsIcon />, route: "/home/settings" },
   
    
  ];

  // Handle Drawer toggle for mobile
  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === "keydown" && ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")) {
      return;
    }
    setState({ right: open });
  };

  // Handle menu item click
  const handleMenuClick = (route: string, text: string) => {
    setSelectedMenu(text);
    navigate(route);
    setState({ right: false });
  };

  // Handle logout
  const handleLogout = () => {
    console.log("User is logging out...");
    localStorage.clear();
    sessionStorage.clear();
    navigate("/home/logout");
  };

  // Set default selected menu based on the current location
  useEffect(() => {
    const activeMenu = menuItems.find((item) => location.pathname.includes(item.route));
    if (activeMenu) {
      setSelectedMenu(activeMenu.text);
    } else {
      // Default to the first menu
      setSelectedMenu(menuItems[0].text);
      navigate(menuItems[0].route);
    }
  }, [location.pathname, menuItems, navigate]);

  const list = () => (
    <Box className="bg-white min-w-[250px] h-full" role="presentation" onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
      <List>
        {menuItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton
              onClick={() => handleMenuClick(item.route, item.text)}
              className={selectedMenu === item.text ? "text-primary font-semibold" : "text-black"}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <ListItem disablePadding>
        <ListItemButton onClick={handleLogout} className="text-black">
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </ListItem>
    </Box>
  );

  return (
    <div>
      {/* Top Bar for Mobile */}
      <div className="lg:hidden flex justify-between items-center bg-gradient-to-b w-full from-primary to-secondary p-4 fixed top-0 z-10">
        <h1 className="text-xl font-bold font-primary text-white"></h1>
        <MenuIcon onClick={toggleDrawer(true)} className="cursor-pointer text-white" />
      </div>

      {/* Desktop Navbar */}
      <div className="hidden lg:flex bg-white shadow-md w-full fixed top-0 left-0 z-20 items-center justify-between px-6 py-3">
        {/* Logo Section */}
        <div className="flex items-center space-x-3">
          <img src={logo} alt="Logo" className="h-12 w-22 rounded-full" />
        </div>

        {/* Centered Menu for Desktop */}
        <div className="flex justify-center space-x-10">
          {menuItems.map((item, index) => (
            <div
              key={index}
              className={`cursor-pointer flex items-center space-x-2 ${
                selectedMenu === item.text ? "text-primary font-semibold border-b-2 border-primary" : "text-gray-600 hover:text-primary"
              }`}
              onClick={() => handleMenuClick(item.route, item.text)}
            >
              <span className="text-lg font-primary">{item.text}</span>
            </div>
          ))}
        </div>

        {/* Right Section with Logout */}
        <div className="flex items-center space-x-4">
          <Tooltip title="Logout">
            <IconButton onClick={handleLogout}>
              <LogoutIcon className="text-gray-500 hover:text-primary" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Drawer for Mobile */}
      <Drawer anchor={"right"} open={state.right} onClose={toggleDrawer(false)}>
        {list()}
      </Drawer>
    </div>
  );
};

export default Navbar;
